import { useContext } from 'react';
import clsx from 'clsx';
import { ListBoxItem, type Selection } from 'react-aria-components';
import { useHistory } from 'react-router-dom';
import {
  LookingForMore,
  useGetOccasionPreferences,
} from '@hungryroot/exp-175-diet-driven-preferences';
import { ThumbsUp } from '@hungryroot/icons';
import { Badge, BadgeVariant, TileList } from '@hungryroot/pho';
import { Paragraph, Tile } from '@hungryroot/ramen';
import { type OccasionPreferenceEntity } from '@hungryroot/rest';
import { ROUTE_EATING_HABITS } from '../../defs/routes';
import { QuizContext } from '../../quiz-context';
import { type QuizPreferenceItem } from '../../segment/quizAnalytics';
import { getOccasionPreferenceDietaryRestrictionParamFromQuiz } from '../../utils/getOccasionPreferenceDietaryRestrictionParamFromQuiz';
import styles from './ControlOccasionPreferenceSelect.module.css';
const DEFAULT_BADGE_FILTERS = ['best price', 'kosher'];

type MealTypeQuestion = 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'sweets';

type Props = {
  testid: string;
  ariaLabel: string;
  question: MealTypeQuestion;
  variant: 'simple' | 'detail';
  selectedKeys?: number[];
  badgeFilters?: string[];
  onTrackSelected?: (items: QuizPreferenceItem[]) => void;
};

function getBadge(badges: string[], filterBadges: string[]) {
  return badges.filter(
    (badge) => !filterBadges.includes(badge.toLowerCase())
  )[0];
}

// Products will show brand name, pairings will show rating and cook time
function getLead({
  brand_name,
  num_ratings,
  recipe_score,
  cooking_time,
}: OccasionPreferenceEntity) {
  const numRatings = num_ratings
    ? cooking_time
      ? ` (${num_ratings}) • `
      : `(${num_ratings})`
    : '';
  const cookTime = cooking_time !== null ? `${cooking_time} min` : '';

  return brand_name ? (
    <Paragraph variant="caption" className={styles.lead}>
      {brand_name}
    </Paragraph>
  ) : (
    <Paragraph variant="caption" className={styles['lead-detail']}>
      {recipe_score && (
        <>
          <ThumbsUp className={styles.icon} /> {Math.round(recipe_score)}%
          {numRatings}
          {cookTime}
        </>
      )}
    </Paragraph>
  );
}

function getMealType(question: MealTypeQuestion) {
  switch (question) {
    case 'snacks':
      return 'snack';
    case 'sweets':
      return 'sweet';
    default:
      return question;
  }
}

export function ControlOccasionPreferenceSelect({
  testid,
  ariaLabel,
  question,
  variant,
  badgeFilters,
  onTrackSelected,
}: Props) {
  const { quiz, onQuestionAnswer } = useContext(QuizContext);
  const dietaryRestrictionParam =
    getOccasionPreferenceDietaryRestrictionParamFromQuiz(quiz);

  const history = useHistory();

  const { data: preferenceOptions } = useGetOccasionPreferences(
    dietaryRestrictionParam
  );

  if (!dietaryRestrictionParam) {
    history.push(ROUTE_EATING_HABITS);
  }

  if (preferenceOptions === undefined) {
    return null;
  }

  const selected = quiz[question] ?? undefined;
  const mealType = getMealType(question);

  return (
    <div>
      <TileList
        testid={testid}
        ariaLabel={ariaLabel}
        variant="listbox"
        selectionMode="multiple"
        items={preferenceOptions[mealType].map((item) => ({
          id: item.object_id,
          heading: item.name ?? '',
        }))}
        selectedKeys={selected}
        onSelectionChange={(keys: Selection) => {
          const value = Array.from(keys) as number[];

          if (onTrackSelected !== undefined) {
            const trackedItems = preferenceOptions[mealType].reduce(
              (acc, item) => {
                if (value.includes(item.object_id)) {
                  acc.push({
                    name: item.name ?? '',
                    id: item.object_id,
                    type: item.type,
                  });
                }
                return acc;
              },
              [] as QuizPreferenceItem[]
            );

            onTrackSelected(trackedItems);
          }

          onQuestionAnswer(quiz, question, value);
        }}
      >
        {preferenceOptions[mealType].map((preferenceOption) => {
          const {
            object_id: id,
            name,
            image_url: src,
            badges,
          } = preferenceOption;

          if (name === null) {
            return null;
          }

          const lead = variant === 'detail' && getLead(preferenceOption);
          const badge =
            variant === 'detail' &&
            getBadge(badges, [
              ...DEFAULT_BADGE_FILTERS,
              ...(badgeFilters || []),
            ]);

          return (
            <ListBoxItem
              key={id}
              id={id}
              textValue={name}
              className={styles.item}
            >
              {({ isSelected, isFocusVisible, isHovered, isPressed }) => (
                <Tile
                  testid={`${testid}-tile-${id}`}
                  heading={name ?? ''}
                  lead={lead}
                  badge={
                    badge && (
                      <Badge
                        variant={BadgeVariant.Misc}
                        className={styles.badge}
                      >
                        <Paragraph variant="caption">{badge}</Paragraph>
                      </Badge>
                    )
                  }
                  imgSrc={src ?? undefined}
                  alignment="center-left-aligned-content"
                  className={clsx({
                    [styles.center]: variant === 'simple',
                  })}
                  isFocusVisible={isFocusVisible || isHovered}
                  isPressed={isPressed}
                  selected={isSelected}
                  slideIn
                />
              )}
            </ListBoxItem>
          );
        })}
      </TileList>

      <LookingForMore className={styles.callout} />
    </div>
  );
}
